import { Controller } from '@hotwired/stimulus';
import Vue from 'vue';

// クーポン配布のターゲット選択フィールドを組み立てるコントローラ
export default class extends Controller {
  static targets = ['mountPoint'];
  static values = {
    shopBranches: Array,
    shopBranchGroups: Array,
    targetAttributes: Object,
    targets: Array,
  };

  connect() {
    const el = this.mountPointTarget;
    const targets = this.targetsValue || [];
    const targetAttributes = {
      ...(this.targetAttributesValue || {}),
      last_visited_easy_setting: '',
      last_visited_from: 0,
    };
    const shopBranches = this.shopBranchesValue || [];
    const shopBranchGroups = this.shopBranchGroupsValue || [];

    this.vm = new Vue({
      el,
      data: {
        shop_branch_groups: [],
        shop_branches: [],
        shop_coupon_distribution: {
          targets: [],
        },
        shop_visited_easy_mode: true,
        shop_visited_easy_mode_label: '詳細指定に切替（上級者向け）',
      },
      mounted() {
        this.initialize();
      },
      methods: {
        // 初期化
        initialize() {
          const throughKeys = [
            'id',
            'last_visited_from',
            'last_visited_shop_branch_ids',
            'visited_cnt_shop_branch_ids',
          ];

          this.shop_branch_groups = shopBranchGroups;
          this.shop_branches = shopBranches;
          this.shop_coupon_distribution.targets = targets.map((target) => {
            for (const key of Object.keys(target)) {
              if (throughKeys.indexOf(key) >= 0) continue;

              // Rails側では true or false なんだけど、JS側では 1 or 0 で扱われる問題のワークアラウンド
              if (key === 'last_visited_only') {
                if (target[key] === true) {
                  target[key] = '1';
                } else if (target[key] === false) {
                  target[key] = '0';
                }
              }

              if (target[key] === 0) {
                // 数値0を文字列にリプレイス(false判定されないための対策)
                target[key] = '0';
              } else if (target[key] === null || target[key] === undefined) {
                // null or undefinedを空文字にリプレイス
                target[key] = '';
              }
            }

            // モデルに選択済みの来店履歴の時間(last_visited_***)を設定
            this.setLastVisitedTime(target);

            target.show = true;
            target._destroy = false;
            target.optional_cond = this.setOptionalCond(target);
            target.last_visited_easy_setting =
              target.last_visited_easy_setting || '';
            target.last_visited_from = target.last_visited_from || 0;

            return target;
          });
        },
        // 来店履歴の時間設定
        setLastVisitedTime(target) {
          if (
            ['visited_count', 'visited_shop'].indexOf(target.type) < 0 ||
            typeof target.last_visited_from !== 'number'
          )
            return;

          if (typeof target.last_visited_to === 'number') {
            const isEasyMode =
              (target.last_visited_to - target.last_visited_from) % 1440 === 0;

            if (isEasyMode) {
              const day = target.last_visited_to / 1440;
              target.last_visited_easy_setting = day;
              this.enableShopVisitedEasyMode(true, target);
            } else {
              this.enableShopVisitedEasyMode(false, target);
            }
          } else {
            this.enableShopVisitedEasyMode(false, target);
          }
        },
        // 来店履歴の簡易モード or 詳細モードを切替
        enableShopVisitedEasyMode(isEasyMode, target) {
          if (isEasyMode) {
            this.shop_visited_easy_mode = true;
            this.shop_visited_easy_mode_label = '詳細指定に切替（上級者向け）';
          } else {
            this.shop_visited_easy_mode = false;
            this.shop_visited_easy_mode_label = '簡易指定に切替';

            if (!target.last_visited_from) {
              target.last_visited_from = 0;
            }
          }
        },
        // 詳細条件表示の初期値設定
        setOptionalCond(target) {
          if (target.type === 'visited_count') {
            return (
              !!target.last_visited_to ||
              target.visited_cnt_shop_branch_ids.length > 0
            );
          } else if (target.type === 'visited_shop') {
            return (
              !!target.last_visited_shop_barcode_group_id ||
              target.last_visited_shop_branch_ids.length > 0
            );
          }

          return false;
        },
        // ターゲット条件フィールドの追加
        appendTarget() {
          const targets = this.shop_coupon_distribution.targets;
          targets.push({
            ...targetAttributes,
            show: true,
            type: '',
            optional_cond: false,
            _destroy: false,
          });
        },
        // ターゲット条件フィールドの削除
        removeTarget(target, index) {
          if (!target.id) {
            this.shop_coupon_distribution.targets.splice(index, 1);
          } else {
            target.show = false;
            target._destroy = true;
            const _target = Object.assign({}, target);

            this.shop_coupon_distribution.targets.splice(index, 1, _target);
          }
        },
        // ターゲット条件フィールドのリセット
        resetTarget(target) {
          const keys = Object.keys(targetAttributes);
          const throughKeys = ['id', 'shop_coupon_distribution_id'];
          const arrayValueKeys = [
            'last_visited_shop_branch_ids',
            'visited_cnt_shop_branch_ids',
          ];

          for (const key of keys) {
            // keyがターゲットのプロパティとして存在しない or リセット対象外の場合は何もしない
            if (!Object.hasOwn(target, key) || throughKeys.indexOf(key) >= 0)
              return;

            if (arrayValueKeys.indexOf(key) >= 0) {
              target[key] = [];
            } else {
              target[key] = '';
            }
          }
        },
        // 全店舗を選択・解除
        setAllShopBranchIds(event, target) {
          if (event.target.checked) {
            target.group_checkboxes = [event.target.value];
            let targetModel = null;

            if (target.type === 'visited_shop') {
              targetModel = target.last_visited_shop_branch_ids || [];
            } else if (target.type === 'visited_count') {
              targetModel = target.visited_cnt_shop_branch_ids || [];
            } else {
              throw new Error('Unknown target type');
            }

            targetModel.splice(0, targetModel.length);
            for (const shop_branch of this.shop_branches) {
              targetModel.push(shop_branch.id);
            }
          } else {
            if (target.type === 'visited_shop') {
              target.last_visited_shop_branch_ids = [];
            } else if (target.type === 'visited_count') {
              target.visited_cnt_shop_branch_ids = [];
            } else {
              throw new Error('Unknown target type');
            }
          }
        },
        // 個別店舗を選択・解除
        setShopBranchIds(event, target, shop_branch_ids) {
          if (event.target.checked) {
            target.group_checkboxes = [event.target.value];
            let targetModel = null;

            if (target.type === 'visited_shop') {
              targetModel = target.last_visited_shop_branch_ids;
            } else if (target.type === 'visited_count') {
              targetModel = target.visited_cnt_shop_branch_ids;
            } else {
              throw new Error('Unknown target type');
            }

            targetModel.splice(0, targetModel.length);
            for (const shop_branch_id of shop_branch_ids) {
              targetModel.push(shop_branch_id);
            }
          } else {
            if (target.type === 'visited_count') {
              target.visited_cnt_shop_branch_ids = [];
            } else {
              throw new Error('Unknown target type');
            }
          }
        },
        // 来店回数・来店履歴の詳細条件の表示 or 非表示を切替
        toggleVisitTargetDetailCond(target) {
          this.toggleTargetDetailCond(target);

          if (target.optional_cond) {
            if (!target.last_visited_from) {
              target.last_visited_from = 0;
            }
          } else {
            target.last_visited_from = '';
          }
        },
        // 詳細条件の表示 or 非表示を切替
        toggleTargetDetailCond(target) {
          target.optional_cond = !target.optional_cond;
        },
        // 簡易表示モードで日数のみ変更すると期間を自動で設定
        changeLastVisitedDate(target) {
          const selected_day = target.last_visited_easy_setting;
          if (!selected_day) {
            target.last_visited_from = '';
            target.last_visited_to = '';
          } else {
            const last_visited_from =
              (Number.parseInt(selected_day) - 1) * 1440;
            target.last_visited_from = String(last_visited_from);
            target.last_visited_to = String(last_visited_from + 1440);
          }
        },
        // 来店履歴の簡易モードと詳細モードを切替
        toggleShopVisitedDetailSetting(target) {
          target.last_visited_easy_setting = '';
          target.last_visited_from = '';
          target.last_visited_to = '';

          this.enableShopVisitedEasyMode(!this.shop_visited_easy_mode, target);
        },
      },
    });
    // チラツキ防止のため、Vueマウント後に表示
    this.element.style.display = 'block';
  }

  // メモリリーク防止のため、Vueインスタンスを破棄
  disconnect() {
    if (this.vm) {
      this.vm.$destroy();
    }
  }
}
